import { ApiError, OfflineError } from '../api/http';
import { ca } from '../i18n/ca';

/**
 * The refusals whose detail is already the Catalan sentence a person reads: a
 * recipe's declaration (ENGINE_DESIGN.md §2.18–§2.20) writes its reason for
 * people, so it is shown as it came, with the step that fixes it.
 */
const SAID_IN_CATALAN = [
  'param_not_admitted',
  'twist_not_admitted',
  'secondary_not_admitted',
  'era_not_admitted',
  'no_civilians',
  'recipe_coming_soon',
];

/** Where the platform said a refusal is fixed, when it said. */
function fixByOf(err: ApiError): string | null {
  const body = err.body;
  if (body && typeof body === 'object' && 'fixBy' in body) {
    const fix = (body as { fixBy?: unknown }).fixBy;
    return typeof fix === 'string' ? fix : null;
  }
  return null;
}

/**
 * The sentence to show, plus what the platform actually said. `not_possible`
 * covers every IllegalStateException, so its Catalan sentence is necessarily
 * vague; the detail behind it is what tells an editor (or whoever reads the
 * screenshot) which of them it was.
 */
export function messageOf(err: unknown): string {
  const said = sentenceOf(err);
  return said.detail ? `${said.text} (${said.detail})` : said.text;
}

/**
 * The same refusal in two parts: the Catalan sentence, complete on its own,
 * and the platform's technical detail when the sentence did not already say
 * it. A member reads the first; the second is for the log, an editor and the
 * dialog's «Detall tècnic» (the owner, 2026-10-11: the English tally in
 * parentheses does not belong on a member's screen).
 */
export function sentenceOf(err: unknown): { text: string; detail: string | null } {
  if (err instanceof OfflineError) return { text: ca.errors.offline, detail: null };
  if (!(err instanceof ApiError)) return { text: ca.errors.unknown, detail: null };
  return { text: catalanOf(err), detail: usesDetail(err) ? null : err.detail };
}

/** Whether the sentence shown is made of the platform's detail itself. */
function usesDetail(err: ApiError): boolean {
  if (!err.detail) return true;
  if (err.code === 'no_place_of_kind') return true;
  if (
    (err.code === 'no_camp_ground' || err.code === 'no_qrf_post') &&
    err.detail.includes(':no_survey')
  ) {
    return true;
  }
  return SAID_IN_CATALAN.includes(err.code) || err.code.startsWith('missing:');
}

function catalanOf(err: ApiError): string {
  // The ground a type needs and the terrain lacks: the detail is the kind
  // (`site`, `airfield`…) and, when the survey is not done, `:no_survey`.
  if (err.code === 'no_place_of_kind' && err.detail) {
    return ca.missionUi.refusedGround(err.detail);
  }
  // night_raid's own camp and reaction-post ground (CampKind, QrfPostKind):
  // the same terrain-needs-a-survey case as no_place_of_kind above, but the
  // detail is the kind's own full reason sentence ("camp:no_survey — …"),
  // not a bare place kind.
  if (
    (err.code === 'no_camp_ground' || err.code === 'no_qrf_post') &&
    err.detail?.includes(':no_survey')
  ) {
    return ca.missionUi.needsSurvey;
  }
  if (err.detail && (SAID_IN_CATALAN.includes(err.code) || err.code.startsWith('missing:'))) {
    const fix = fixByOf(err);
    const how = fix ? ca.missionUi.fixBy[fix] : undefined;
    return how ? `${err.detail} ${how}` : err.detail;
  }
  return ca.errors[err.code] ?? ca.errors.unknown;
}

/** One thing the platform worked out would succeed where it refused (`alternatives` of the refusal). */
export interface RefusalAlternative {
  /** `means`: another way in for the same objective; `objective`: another objective; `startPoint`: the creator's own point. */
  kind: 'means' | 'objective' | 'startPoint';
  /** The way in (`helicopter`, `vehicle`, `foot`) or the objective's name. */
  value?: string;
}

/** The alternatives a refusal names, in the platform's order; none when it worked nothing out. */
function alternativesOf(err: ApiError): RefusalAlternative[] {
  const body = err.body;
  if (!body || typeof body !== 'object' || !('alternatives' in body)) return [];
  const list = (body as { alternatives?: unknown }).alternatives;
  if (!Array.isArray(list)) return [];
  const out: RefusalAlternative[] = [];
  for (const item of list) {
    if (typeof item !== 'string') continue;
    if (item === 'start_point') out.push({ kind: 'startPoint' });
    else if (item.startsWith('means:')) out.push({ kind: 'means', value: item.slice(6) });
    else if (item.startsWith('objective:')) out.push({ kind: 'objective', value: item.slice(10) });
  }
  return out;
}

/** The request parameter a `param_not_admitted` names (release D, D5.1), when the platform said. */
function paramOf(err: ApiError): string | null {
  const body = err.body;
  if (body && typeof body === 'object' && 'param' in body) {
    const param = (body as { param?: unknown }).param;
    return typeof param === 'string' ? param : null;
  }
  return null;
}

/** A refusal as the last step shows it: the sentence, and the step that fixes it when the platform said. */
export interface Refusal {
  text: string;
  /** server · type · map · enemy · ours · start · decide · scan · none, or null when not said. */
  fixBy: string | null;
  /** The request parameter the refusal is about (`weather`, `enemyFaction`, `settings.<id>`…), or null. */
  param?: string | null;
  /** What the platform said beyond the sentence, in its own words: shown only behind «Detall tècnic». */
  detail?: string | null;
  /** What would work instead, when the platform worked it out. */
  alternatives?: RefusalAlternative[];
}

/**
 * The refusal shape of ENGINE_DESIGN.md §2.20 for the wizard's last step: the
 * Catalan reason as it came, and where it is fixed, which the step turns into
 * a link to that step instead of a sentence about it.
 */
export function refusalOf(err: unknown): Refusal {
  if (!(err instanceof ApiError)) return { text: messageOf(err), fixBy: null, param: null };
  const fix = fixByOf(err);
  const param = paramOf(err);
  const said = SAID_IN_CATALAN.includes(err.code) || err.code.startsWith('missing:');
  if (fix && err.detail && said) return { text: err.detail, fixBy: fix, param };
  const sentence = sentenceOf(err);
  return {
    text: sentence.text,
    detail: sentence.detail,
    fixBy: fix,
    param,
    alternatives: alternativesOf(err),
  };
}

/**
 * Where a `fixBy` is fixed in the wizard: the step's own route, the terrain's
 * page in the catalogue for a scan; null for this step and for none.
 */
export function fixByRoute(fixBy: string | null | undefined, world?: string | null): string | null {
  switch (fixBy) {
    case 'server':
      return '/new';
    case 'type':
      return '/new/type';
    case 'map':
      return '/new/map';
    case 'enemy':
      return '/new/enemy';
    case 'ours':
      return '/new/forces';
    case 'start':
      return '/new/start';
    case 'scan':
      return world ? `/catalog/terrain/${encodeURIComponent(world)}` : '/catalog';
    default:
      return null;
  }
}
